import { useState } from 'react';
import { useLoaderData } from 'react-router';
import type { LoaderFunctionArgs } from 'react-router';
import { 
  BarChart3, 
  TrendingUp, 
  Eye, 
  Bookmark, 
  Send, 
  Award,
  Calendar
} from 'lucide-react';
import { requireUser } from '../../lib/session.server';
import { Card, Badge } from '../../components/ui';
import type { User, AnalyticsData } from '../../types';

interface AnalyticsPageData {
  user: User;
  analytics: AnalyticsData;
  dateRange: string;
}

// Mock analytics data
const mockAnalytics: AnalyticsData = {
  overview: {
    total_applications: 8,
    total_views: 156,
    total_saves: 23,
    total_offers: 2,
    response_rate: 0.375, // 3/8 applications got responses
    interview_rate: 0.25, // 2/8 applications got interviews
    offer_rate: 0.25, // 2/8 applications got offers
    avg_response_time: 5.2, // days
  },
  trends: {
    weekly_applications: [1, 2, 1, 3, 1, 0, 0],
    success_by_strategy: [
      { strategy: 'HIGH_COMP', success_rate: 0.67, total_applications: 3 },
      { strategy: 'EASY_WIN', success_rate: 0.25, total_applications: 4 },
      { strategy: 'SMALL_CO', success_rate: 0.5, total_applications: 1 },
      { strategy: 'REMOTE', success_rate: 0, total_applications: 0 }
    ]
  },
  insights: {
    top_companies: [
      { name: 'TechCorp', applications: 2, success_rate: 0.5, views: 15 },
      { name: 'StartupCo', applications: 1, success_rate: 1.0, views: 12 },
      { name: 'DataFlow', applications: 1, success_rate: 0.0, views: 10 },
      { name: 'CloudTech', applications: 1, success_rate: 0.0, views: 8 },
      { name: 'AI Systems', applications: 1, success_rate: 1.0, views: 7 }
    ],
    top_locations: [
      { location: 'Remote', applications: 3, avg_salary: 165000 },
      { location: 'San Francisco, CA', applications: 2, avg_salary: 180000 },
      { location: 'New York, NY', applications: 2, avg_salary: 170000 },
      { location: 'Austin, TX', applications: 1, avg_salary: 140000 }
    ]
  },
  time_series: {
    daily_views: [12, 8, 15, 23, 18, 31, 25, 14, 19, 22, 16, 28, 20, 17],
    daily_saves: [2, 1, 3, 4, 2, 5, 3, 1, 2, 3, 2, 4, 2, 1],
    daily_applications: [0, 0, 1, 2, 0, 1, 1, 0, 1, 1, 0, 1, 0, 0],
    labels: ['Jan 1', 'Jan 2', 'Jan 3', 'Jan 4', 'Jan 5', 'Jan 6', 'Jan 7', 'Jan 8', 'Jan 9', 'Jan 10', 'Jan 11', 'Jan 12', 'Jan 13', 'Jan 14']
  },
  strategy_performance: [
    { strategy: 'HIGH_COMP', queries: 12, hits: 45, applications: 3, success_rate: 0.67 },
    { strategy: 'EASY_WIN', queries: 8, hits: 78, applications: 4, success_rate: 0.25 },
    { strategy: 'SMALL_CO', queries: 6, hits: 23, applications: 1, success_rate: 0.5 },
    { strategy: 'REMOTE', queries: 5, hits: 34, applications: 0, success_rate: 0 }
  ]
};

export async function loader({ request }: LoaderFunctionArgs) {
  const user = await requireUser(request);
  const url = new URL(request.url);
  const dateRange = url.searchParams.get('range') || '30d';

  try {
    // In a real implementation, this would fetch from the analytics API
    // const analytics = await analyticsApi.getAnalytics(user.id, { range: dateRange });
    
    return {
      user,
      analytics: mockAnalytics,
      dateRange,
    };
  } catch (error) {
    return {
      user,
      analytics: mockAnalytics,
      dateRange,
      error: 'Failed to load analytics data',
    };
  }
}

export default function AnalyticsPage() {
  const { analytics, dateRange } = useLoaderData() as AnalyticsPageData;
  const [selectedChart, setSelectedChart] = useState<'views' | 'saves' | 'applications'>('views');
  
  // Safe access with defaults
  const defaultOverview = {
    total_applications: 0,
    total_views: 0,
    total_saves: 0,
    total_offers: 0,
    response_rate: 0,
    interview_rate: 0,
    offer_rate: 0,
    avg_response_time: 0,
  };

  const defaultTrends = {
    weekly_applications: [],
    success_by_strategy: [],
  };

  const defaultInsights = {
    top_companies: [],
    top_locations: [],
  };

  const safeAnalytics = {
    overview: { ...defaultOverview, ...analytics?.overview },
    time_series: analytics?.time_series || {
      daily_views: [],
      daily_saves: [],
      daily_applications: [],
      labels: []
    },
    strategy_performance: analytics?.strategy_performance || [],
    trends: { ...defaultTrends, ...analytics?.trends },
    insights: { ...defaultInsights, ...analytics?.insights }
  };


  const formatNumber = (num: number) => {
    if (num >= 1000) {
      return `${(num / 1000).toFixed(1)}k`;
    }
    return num.toString();
  };

  const formatPercentage = (rate: number) => {
    return `${(rate * 100).toFixed(1)}%`;
  };

  const SimpleChart = ({ data, color, height = 60 }: { data: number[], color: string, height?: number }) => {
    const max = Math.max(...data);
    const min = Math.min(...data);
    const range = max - min || 1;
    
    return (
      <div className="flex items-end gap-1" style={{ height }}>
        {data.map((value, index: number) => {
          const normalizedHeight = ((value - min) / range) * (height - 10) + 10;
          return (
            <div
              key={index}
              className={`${color} rounded-t transition-all hover:opacity-80`}
              style={{ 
                height: `${normalizedHeight}px`,
                width: `${100 / data.length}%`,
                minHeight: '4px'
              }}
              title={`${safeAnalytics.time_series.labels[index]}: ${value}`}
            />
          );
        })}
      </div>
    );
  };

  const kpis = [
    {
      label: 'Job Views',
      value: safeAnalytics.overview.total_views,
      icon: Eye,
      color: 'text-info',
      bgColor: 'bg-info/10',
      change: '+12%',
      changeColor: 'text-positive'
    },
    {
      label: 'Jobs Saved',
      value: safeAnalytics.overview.total_saves,
      icon: Bookmark,
      color: 'text-primary',
      bgColor: 'bg-primary/10',
      change: '+8%',
      changeColor: 'text-positive'
    },
    {
      label: 'Applications',
      value: safeAnalytics.overview.total_applications,
      icon: Send,
      color: 'text-positive',
      bgColor: 'bg-positive/10',
      change: '+25%',
      changeColor: 'text-positive'
    },
    {
      label: 'Offers',
      value: safeAnalytics.overview.total_offers,
      icon: Award,
      color: 'text-caution',
      bgColor: 'bg-caution/10',
      change: '+100%',
      changeColor: 'text-positive'
    }
  ];

  const chartData = {
    views: safeAnalytics.time_series.daily_views,
    saves: safeAnalytics.time_series.daily_saves,
    applications: safeAnalytics.time_series.daily_applications
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <div className="flex items-center gap-2 mb-2">
          <BarChart3 className="h-6 w-6 text-primary" />
          <h1 className="text-3xl font-bold text-foreground">Analytics</h1>
        </div>
        <p className="text-muted-foreground">
          Track your job search performance with key metrics and insights.
        </p>
      </div>

      {/* Date Range Selector */}
      <Card>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Calendar className="h-4 w-4 text-muted-foreground" />
            <span className="text-sm font-medium text-foreground">
              Last {dateRange === '7d' ? '7 days' : dateRange === '30d' ? '30 days' : '90 days'}
            </span>
          </div>
          <div className="flex items-center gap-2">
            {(['7d', '30d', '90d'] as const).map((range) => (
              <button
                key={range}
                className={`px-3 py-1 rounded-lg text-sm transition-all focus:outline-none focus:ring-2 focus:ring-primary/40 ${
                  dateRange === range
                    ? 'bg-primary text-primary-foreground'
                    : 'text-muted-foreground hover:text-foreground hover:bg-muted/50'
                }`}
                onClick={() => {
                  const url = new URL(window.location.href);
                  url.searchParams.set('range', range);
                  window.location.href = url.toString();
                }}
              >
                {range === '7d' ? '7D' : range === '30d' ? '30D' : '90D'}
              </button>
            ))}
          </div>
        </div>
      </Card>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {kpis.map((kpi) => {
          const IconComponent = kpi.icon;
          return (
            <Card key={kpi.label} className="border border-border hover:border-border-hover hover:shadow-card-hover focus-within:ring-2 focus-within:ring-primary/40 active:bg-muted/50 transition-all">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">{kpi.label}</p>
                  <p className="text-2xl font-bold text-foreground mt-1">
                    {formatNumber(kpi.value)}
                  </p>
                  <div className="flex items-center gap-1 mt-2">
                    <TrendingUp className="h-3 w-3 text-positive" />
                    <span className={`text-xs ${kpi.changeColor}`}>{kpi.change}</span>
                    <span className="text-xs text-muted-foreground">vs last period</span>
                  </div>
                </div>
                <div className={`p-2 rounded-lg ${kpi.bgColor}`}>
                  <IconComponent className={`h-5 w-5 ${kpi.color}`} />
                </div>
              </div>
            </Card>
          );
        })}
      </div>

      {/* Performance Metrics */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card className="border border-border hover:border-border-hover hover:shadow-card-hover focus-within:ring-2 focus-within:ring-primary/40 transition-all">
          <div className="text-center">
            <div className="text-2xl font-bold text-foreground">
              {formatPercentage(safeAnalytics.overview.response_rate)}
            </div>
            <div className="text-sm text-muted-foreground">Response Rate</div>
            <div className="text-xs text-muted-foreground mt-1">
              {Math.round(safeAnalytics.overview.response_rate * safeAnalytics.overview.total_applications)} of {safeAnalytics.overview.total_applications} applications
            </div>
          </div>
        </Card>

        <Card className="border border-border hover:border-border-hover hover:shadow-card-hover focus-within:ring-2 focus-within:ring-primary/40 transition-all">
          <div className="text-center">
            <div className="text-2xl font-bold text-foreground">
              {formatPercentage(safeAnalytics.overview.offer_rate)}
            </div>
            <div className="text-sm text-muted-foreground">Offer Rate</div>
            <div className="text-xs text-muted-foreground mt-1">
              {safeAnalytics.overview.total_offers} offers received
            </div>
          </div>
        </Card>

        <Card className="border border-border hover:border-border-hover hover:shadow-card-hover focus-within:ring-2 focus-within:ring-primary/40 transition-all">
          <div className="text-center">
            <div className="text-2xl font-bold text-foreground">
              {safeAnalytics.overview.avg_response_time.toFixed(1)}d
            </div>
            <div className="text-sm text-muted-foreground">Avg Response Time</div>
            <div className="text-xs text-muted-foreground mt-1">
              From application to response
            </div>
          </div>
        </Card>
      </div>

      {/* Activity Chart */}
      <Card>
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold text-foreground">Activity Trends</h2>
            <div className="flex items-center gap-2">
              {(['views', 'saves', 'applications'] as const).map((type) => (
                <button
                  key={type}
                  onClick={() => setSelectedChart(type)}
                  className={`px-3 py-1 rounded-lg text-sm transition-all capitalize focus:outline-none focus:ring-2 focus:ring-primary/40 ${
                    selectedChart === type
                      ? 'bg-primary text-primary-foreground'
                      : 'text-muted-foreground hover:text-foreground hover:bg-muted/50'
                  }`}
                >
                  {type}
                </button>
              ))}
            </div>
          </div>

          <div className="space-y-3">
            <div className="text-center">
              <div className="text-2xl font-bold text-foreground">
                {selectedChart === 'views' && formatNumber(safeAnalytics.overview.total_views)}
                {selectedChart === 'saves' && formatNumber(safeAnalytics.overview.total_saves)}
                {selectedChart === 'applications' && formatNumber(safeAnalytics.overview.total_applications)}
              </div>
              <div className="text-sm text-muted-foreground capitalize">
                Total {selectedChart} in last {dateRange === '7d' ? '7 days' : '30 days'}
              </div>
            </div>

            <SimpleChart 
              data={chartData[selectedChart]} 
              color={
                selectedChart === 'views' ? 'bg-info' :
                selectedChart === 'saves' ? 'bg-primary' : 'bg-positive'
              }
              height={80}
            />

            <div className="flex justify-between text-xs text-muted-foreground">
              <span>{safeAnalytics.time_series.labels[0]}</span>
              <span>{safeAnalytics.time_series.labels[safeAnalytics.time_series.labels.length - 1]}</span>
            </div>
          </div>
        </div>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Top Companies */}
        <Card className="border border-border hover:border-border-hover hover:shadow-card-hover focus-within:ring-2 focus-within:ring-primary/40 transition-all">
          <div className="space-y-4">
            <h2 className="text-lg font-semibold text-foreground">Top Companies</h2>
            <div className="space-y-3">
              {safeAnalytics.insights.top_companies.map((company, index: number) => (
                <div key={company.name} className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="text-sm font-medium text-muted-foreground">
                      #{index + 1}
                    </div>
                    <div>
                      <div className="font-medium text-foreground">{company.name}</div>
                      <div className="text-xs text-muted-foreground">
                        {company.applications} applications • {(company.success_rate * 100).toFixed(0)}% success
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className={`w-20 h-2 bg-muted/30 rounded-full overflow-hidden`}>
                      <div 
                        className="h-full bg-primary transition-all"
                        style={{ width: `${(company.views || 0) / Math.max(safeAnalytics.insights.top_companies[0]?.views || 1, 1) * 100}%` }}
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </Card>

        {/* Strategy Performance */}
        <Card className="border border-border hover:border-border-hover hover:shadow-card-hover focus-within:ring-2 focus-within:ring-primary/40 transition-all">
          <div className="space-y-4">
            <h2 className="text-lg font-semibold text-foreground">Strategy Performance</h2>
            <div className="space-y-3">
              {(safeAnalytics.trends?.success_by_strategy || []).map((strategy: any) => (
                <div key={strategy.strategy} className="space-y-2">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Badge variant="info" className="text-xs">
                        {strategy.strategy}
                      </Badge>
                      <span className="text-sm text-muted-foreground">
                        {strategy.total_applications} applications
                      </span>
                    </div>
                    <div className="text-sm font-medium text-foreground">
                      {formatPercentage(strategy.success_rate)}
                    </div>
                  </div>
                  <div className="w-full bg-muted/30 rounded-full h-1.5">
                    <div 
                      className="h-full bg-positive rounded-full transition-all"
                      style={{ width: `${strategy.success_rate * 100}%` }}
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </Card>

        {/* Geographic Distribution */}
        <Card className="border border-border hover:border-border-hover hover:shadow-card-hover focus-within:ring-2 focus-within:ring-primary/40 transition-all">
          <div className="space-y-4">
            <h2 className="text-lg font-semibold text-foreground">Job Locations</h2>
            <div className="space-y-3">
              {safeAnalytics.insights.top_locations.map((location: any) => (
                <div key={location.location} className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="font-medium text-foreground">{location.location}</div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="text-sm text-muted-foreground">
                      {location.applications} applications
                    </div>
                    <div className="text-sm font-medium text-foreground">
                      ${Math.round(location.avg_salary / 1000)}k avg
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </Card>

        {/* Quick Stats */}
        <Card className="border border-border hover:border-border-hover hover:shadow-card-hover focus-within:ring-2 focus-within:ring-primary/40 transition-all">
          <div className="space-y-4">
            <h2 className="text-lg font-semibold text-foreground">Quick Stats</h2>
            <div className="grid grid-cols-2 gap-4">
              <div className="text-center">
                <div className="text-lg font-bold text-foreground">
                  {safeAnalytics.overview.total_views > 0 ? (safeAnalytics.overview.total_saves / safeAnalytics.overview.total_views * 100).toFixed(1) : '0.0'}%
                </div>
                <div className="text-xs text-muted-foreground">Save Rate</div>
              </div>
              <div className="text-center">
                <div className="text-lg font-bold text-foreground">
                  {safeAnalytics.overview.total_saves > 0 ? (safeAnalytics.overview.total_applications / safeAnalytics.overview.total_saves * 100).toFixed(1) : '0.0'}%
                </div>
                <div className="text-xs text-muted-foreground">Apply Rate</div>
              </div>
              <div className="text-center">
                <div className="text-lg font-bold text-foreground">
                  {safeAnalytics.strategy_performance.length}
                </div>
                <div className="text-xs text-muted-foreground">Active Strategies</div>
              </div>
              <div className="text-center">
                <div className="text-lg font-bold text-foreground">
                  {safeAnalytics.time_series.daily_views.length > 0 ? Math.round(safeAnalytics.time_series.daily_views.reduce((a, b) => a + b, 0) / safeAnalytics.time_series.daily_views.length) : 0}
                </div>
                <div className="text-xs text-muted-foreground">Daily Avg Views</div>
              </div>
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
}